import React, { useEffect, useState, useRef } from "react";
import "../../assets/matka/css/matka.css";
import toast from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import socket from "../../assets/socket";

const base_url = process.env.REACT_APP_API_URL;

const MatkaList = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [markets, setMarkets] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userBalance, setUserBalance] = useState(0);

  const observerRef = useRef(null);

  /* ---------------- USER BALANCE ---------------- */

  const fetchUserBalance = async () => {
    try {
      const res = await axios.get(`${base_url}/api/matka/user/balance`, {
        headers: { Authorization: token },
      });

      if (res.data.success) {
        setUserBalance(res.data.balance || 0);
      }
    } catch {
      console.log("Failed to load balance");
    }
  };

  /* ---------------- FETCH MARKETS ---------------- */

  const fetchMarkets = async (pageNo = 1) => {
    if (loading || (!hasMore && pageNo !== 1)) return;

    setLoading(true);

    try {
      const res = await axios.get(
        `${base_url}/api/matka/markets?page=${pageNo}&limit=100`,
      );

      if (res.data.success) {
        if (pageNo === 1) {
          /* Replace markets on refresh */
          setMarkets(res.data.markets);
        } else {
          /* Append next page safely */
          setMarkets((prev) => {
            const merged = [...prev, ...res.data.markets];

            /* remove duplicates */
            const unique = merged.filter(
              (item, index, self) =>
                index === self.findIndex((m) => m.id === item.id),
            );

            return unique;
          });
        }

        setHasMore(res.data.pagination?.hasMore || false);
        setPage(pageNo);
      }
    } catch {
      console.log("Failed to load markets");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    fetchUserBalance();
    fetchMarkets(1);
  }, []);

  /* ---------------- INFINITE SCROLL ---------------- */

  useEffect(() => {
    if (!observerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          fetchMarkets(page + 1);
        }
      },
      { threshold: 1 },
    );

    observer.observe(observerRef.current);

    return () => observer.disconnect();
  }, [page, hasMore, loading]);

  /* ---------------- SOCKET REFRESH ---------------- */

  const refreshMarkets = async () => {
    await fetchMarkets(1);
  };

  useEffect(() => {
    if (!socket) return;

    socket.on("matka_update", refreshMarkets);
    socket.on("matka_result_update", refreshMarkets);
    socket.on("matka_status_update", refreshMarkets);

    return () => {
      socket.off("matka_update", refreshMarkets);
      socket.off("matka_result_update", refreshMarkets);
      socket.off("matka_status_update", refreshMarkets);
    };
  }, []);

  /* ---------------- NAVIGATION ---------------- */

  const handleCardClick = (market) => {
    const slug = market.market_name.toLowerCase().replace(/\s+/g, "-");

    navigate(`/matka/${slug}`, {
      state: {
        marketId: market.id,
        marketName: market.market_name,
        marketOpen: market.open_time,
        marketClose: market.close_time,
      },
    });
  };

  /* ---------------- TIME FORMAT ---------------- */

  const formatTimeAMPM = (time) => {
    if (!time) return "";

    const [hour, minute] = time.split(":");
    let h = parseInt(hour, 10);

    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;

    return `${h}:${minute} ${ampm}`;
  };

  return (
    <div className="matka-screen">
      {/* HEADER */}

      <div className="matka-header">
        <div className="matka-header-left">
          <ArrowLeft
          // onClick={() => 
          //   navigate(-1)
          //   }
          />
          <h3>MATKA LIST</h3>
        </div>

        <div className="matka-header-right">
          <h3>POINTS: {parseFloat(userBalance).toFixed(2)}</h3>
        </div>
      </div>

      {/* MARKET LIST */}

      <div className="matka-content">
        {[...markets]
          .sort((a, b) => b.status - a.status)
          .map((item, index) => {
            const isOpen = item.status === 1;

            return (
              <div
                key={`${item.id}-${index}`}
                className="matka-card"
                onClick={() => handleCardClick(item)}
              >
                {/* LEFT */}

                <div className="card-left">
                  <div
                    className="chart-icon"
                    onClick={(e) => {
                      e.stopPropagation();

                      navigate(`/matka/${item.id}/calendar`, {
                        state: {
                          marketId: item.id,
                          marketName: item.market_name,
                        },
                      });
                    }}
                  >
                    <div className="chart-icon-inner">
                      <div>📊</div>
                      <div className="chart-icon-inner-text">चार्ट</div>
                    </div>
                  </div>
                </div>

                {/* CENTER */}

                <div className="card-center">
                  <h4>{item.market_name}</h4>

                  <p className="result">
                    {item.result ? item.result : "***-**-***"}
                  </p>

                  <div className="time-row">
                    <span>Open: {formatTimeAMPM(item.open_time)}</span>
                    <span>Close: {formatTimeAMPM(item.close_time)}</span>
                  </div>
                </div>

                {/* RIGHT */}

                <div className="card-right">
                  <p className="result-time">
                    ⏱️ रिजल्ट समय <br />
                    {item?.result_time
                      ? formatTimeAMPM(item.result_time)
                      : "जल्द आएगा"}
                  </p>

                  <span
                    className={`status ${isOpen ? "open blink" : "closed"}`}
                  >
                    {isOpen ? "🟢 Open" : "🔴 Closed"}
                  </span>
                </div>
              </div>
            );
          })}

        {loading && <p className="text-center">Loading more...</p>}

        {/* Scroll trigger */}
        <div ref={observerRef} style={{ height: 1 }} />
      </div>
    </div>
  );
};

export default MatkaList;
